<template>
  <CustomModal ref="modal" class="edit-client-modal" title="Modifier un client">
    <CustomForm ref="form" :inputs=inputs :submit=buttons.submit>
      <div class="client-form">

        <div class="client-sections">
          <ClientAccordionSection :step=1 title="Identité" :summary=identitySummary :is-open="openSection === 'identity'" :is-done=isIdentityDone :toggle-callback="() => toggleSection('identity')" :next-callback="() => openSection = 'address'" :is-next-disabled="!isIdentityDone">
            <div class="span-2 type-picker-row">
              <ClientTypePicker :content=inputs.type />
            </div>
            <CustomTextField class="span-2" :content=inputs.name />
            <div v-if="isCompanyIdentifierDisplayed || isVatStatusDisplayed" class="span-2 identifier-row">
              <CustomTextField v-if="isCompanyIdentifierDisplayed" :content=inputs.siret />
              <CustomTextField v-if="isCompanyIdentifierDisplayed" :content=inputs.siren />
              <CustomSelectField v-if="isVatStatusDisplayed" :content=inputs.vatStatus />
            </div>
            <p v-if="isVatStatusDisplayed" class="span-2 field-hint">Un client en franchise en base de TVA reste assujetti.</p>
          </ClientAccordionSection>

          <ClientAccordionSection :step=2 title="Adresse de facturation" :summary=addressSummary :is-open="openSection === 'address'" :is-done=isAddressDone :is-optional="true" :toggle-callback="() => toggleSection('address')" :next-callback="() => openSection = 'contact'">
            <CustomAddressField class="span-2" ref="addressField" :content=inputs.structuredAddress :client-type=inputs.type.value collapsible @update:value="value => inputs.address.value = value" />
          </ClientAccordionSection>

          <ClientAccordionSection :step=3 title="Coordonnées" :summary=contactSummary :is-open="openSection === 'contact'" :is-done=isContactDone :is-optional="true" :toggle-callback="() => toggleSection('contact')" :next-callback="() => openSection = 'followUp'">
            <CustomTextField :content=inputs.phone />
            <CustomTextField :content=inputs.mail />
            <CustomTextField :content=inputs.contact />
            <CustomTextField :content=inputs.other />
            <CustomTextAreaField v-if="inputs.clientInformation.value" class="span-2" :content=inputs.clientInformation style="height: 100px"/>
          </ClientAccordionSection>

          <ClientAccordionSection :step=4 title="Suivi commercial" :summary=followUpSummary :is-open="openSection === 'followUp'" :is-done=isFollowUpDone :is-optional="true" :toggle-callback="() => toggleSection('followUp')">
            <ClientStatusStepper class="span-2" :content=inputs.status />
            <ClientLastContact class="span-2" :content=inputs.lastContact />
            <CustomTextField class="span-2" :content=inputs.description />
            <CustomTextAreaField class="span-2" :content=inputs.note :auto-grow="true" :min-height="88"/>
          </ClientAccordionSection>
        </div>

        <div class="client-form-footer">
          <!-- Archivage : un réglage à part de la saisie de la fiche, en pied de modale -->
          <div v-if="isStateSectionDisplayed" class="footer-archive">
            <button v-if="!isArchived" type="button" class="footer-archive-button" title="Archiver ce client" aria-label="Archiver ce client" @click="archiveClient()">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="2" y="3" width="20" height="5" rx="1"/><path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8"/><path d="M10 12h4"/></svg>
            </button>
            <template v-else>
              <button type="button" class="footer-archive-button" title="Désarchiver ce client" aria-label="Désarchiver ce client" @click="unarchiveClient()">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><path d="M3 3v5h5"/></svg>
              </button>
              <button type="button" class="footer-archive-button is-danger" title="Supprimer définitivement ce client" aria-label="Supprimer définitivement ce client" @click="deleteClient()">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
              </button>
            </template>
          </div>
          <span class="footer-status" :class="{'is-complete': missingRequiredCount === 0}">
            <span v-if="missingRequiredCount === 0" class="footer-status-check" aria-hidden="true"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg></span>
            <span class="footer-status-text">{{ missingRequiredText }}</span>
          </span>
          <CustomButton class="footer-cancel" :is-secondary="true" @click="closeModal()" :params=buttons.cancel />
          <CustomButton class="footer-submit" @click="submitForm" :params=buttons.submit />
        </div>

        <ConfirmModal ref="archiveConfirmModal" title="Archiver ce client" text="Vous allez archiver ce client. Vous le retrouverez dans les clients archivés (rubrique Non intéressés). Ce n’est pas définitif : vous pourrez le désarchiver à tout moment." yes="Continuer" no="Retour"/>
        <ConfirmModal ref="deleteConfirmModal" title="Supprimer ce client" :text="deleteConfirmText" yes="Supprimer" no="Retour"/>
      </div>
    </CustomForm>
  </CustomModal>
</template>

<script>
import {NotificationService} from "@vues/v-common";
import CustomButton from "@/components/forms/customs/customButton/CustomButton.vue";
import CustomTextField from "@/components/forms/customs/customTextField/CustomTextField.vue";
import {ClientController} from "@/controllers/business/ClientController";
import {CLIENT_TYPE_ENUM, CLIENT_TYPE_ENUM_ARRAY, CLIENT_VAT_STATUS_ENUM, CLIENT_VAT_STATUS_ENUM_ARRAY, formatIdentifier, isClientVatStatusDisplayed, normalizeIdentifier, SIREN_MASK, SIRET_MASK, sirenFromSiret} from "@/utils";
import CustomForm from "@/components/forms/CustomForm.vue";
import CustomModal from "@/components/modals/CustomModal.vue";
import {ACTIVATION_STATUS_ENUM} from "@/models/activationStatusEnum";
import ConfirmModal from "@/components/modals/confirmModal.vue";
import CustomTextAreaField from "@/components/forms/customs/customTextAreaField/CustomTextAreaField.vue";
import {CLIENT_STATUS_ENUM_ARRAY} from "@/models/clientStatusEnum";
import CustomSelectField from "@/components/forms/customs/customSelectField/CustomSelectField.vue";
import CustomAddressField from "@/components/forms/customs/customAddressField/CustomAddressField.vue";
import {formatAddressLine} from "@/components/forms/customs/customAddressField/addressSearch";
import ClientAccordionSection from "@/components/forms/ClientAccordionSection.vue";
import ClientTypePicker from "@/components/forms/ClientTypePicker.vue";
import ClientStatusStepper from "@/components/forms/ClientStatusStepper.vue";
import ClientLastContact from "@/components/forms/ClientLastContact.vue";
import {RightService} from "@/services/RightService";

// Libellé d'une valeur d'énumération ({label, value}) ; vide si la valeur est inconnue
const DELETE_CONFIRM_TEXT = "La suppression est définitive. Êtes-vous sûr de vouloir supprimer ce client ? Vous ne pourrez plus revenir en arrière.";
const labelOf = (values, value) => (values.find(option => option.value === value) || {}).label || '';

// Champ obligatoire vide ou valeur trop courte (même règle que CustomForm)
const isInputMissing = input => (input.required && (input.value === '' || input.value === undefined || input.value === null))
  || !!(input.minDigits && input.value && String(input.value).replace(/\D/g, '').length < input.minDigits);

export default {
  name: 'EditClientModal',
  computed: {
    ClientController() { return ClientController },
    CLIENT_TYPE_ENUM() { return CLIENT_TYPE_ENUM },
    isVatStatusDisplayed() { return isClientVatStatusDisplayed(this.inputs.type.value) },
    // SIRET et SIREN : entreprises françaises et internationales, ou tant qu'aucun type n'est enregistré (on doit pouvoir reconnaître le client)
    isCompanyIdentifierDisplayed() {
      return !this.inputs.type.value || this.inputs.type.value === CLIENT_TYPE_ENUM.FRENCH_COMPANY.value || this.inputs.type.value === CLIENT_TYPE_ENUM.INTERNATIONAL_COMPANY.value
    },
    // Archiver, désarchiver et supprimer : réservé aux modérateurs et plus
    isStateSectionDisplayed() { return RightService.isAtLeastModerator() },
    isArchived() { return this.inputs.state.value === ACTIVATION_STATUS_ENUM.DEACTIVATED.value },
    // Même règle que CustomForm : champ obligatoire vide ou SIRET incomplet
    missingRequiredCount() {
      return Object.values(this.inputs).filter(input => input && isInputMissing(input)).length;
    },
    missingRequiredText() {
      const count = this.missingRequiredCount;
      if (count === 0) { return 'Tous les champs obligatoires sont remplis' }
      return count === 1 ? '1 champ obligatoire restant' : `${count} champs obligatoires restants`;
    },
    // Section « Identité » terminée : type, nom et SIRET complet s'il est saisi (il reste facultatif)
    isIdentityDone() {
      return ![this.inputs.type, this.inputs.name, this.inputs.siret].some(isInputMissing);
    },
    identitySummary() {
      return [this.inputs.name.value, labelOf(CLIENT_TYPE_ENUM_ARRAY, this.inputs.type.value), this.isCompanyIdentifierDisplayed ? formatIdentifier(this.inputs.siret.value, SIRET_MASK) : ''].filter(Boolean).join(' · ');
    },
    isAddressDone() { return !!this.inputs.address.value },
    addressSummary() { return formatAddressLine(this.inputs.address.value) || 'Rue, code postal, ville' },
    isContactDone() {
      return [this.inputs.phone, this.inputs.mail, this.inputs.contact, this.inputs.other].some(input => !!input.value);
    },
    contactSummary() {
      return [this.inputs.phone.value, this.inputs.mail.value, this.inputs.contact.value].filter(Boolean).join(' · ') || 'Téléphone, mail, contact';
    },
    isFollowUpDone() { return !!(this.inputs.lastContact.value || this.inputs.note.value || this.inputs.description.value) },
    followUpSummary() {
      const lastContact = this.inputs.lastContact.value ? new Date(this.inputs.lastContact.value) : null;
      const parts = [labelOf(CLIENT_STATUS_ENUM_ARRAY, this.inputs.status.value)];
      if (lastContact && !isNaN(lastContact)) { parts.push(`Dernier contact le ${lastContact.toLocaleDateString('fr-FR')}`) }
      if (this.inputs.note.value) { parts.push('note renseignée') }
      return parts.filter(Boolean).join(' · ');
    },
  },
  props: ['callback'],
  components: {
    CustomAddressField,
    ClientAccordionSection, ClientTypePicker, ClientStatusStepper, ClientLastContact, CustomSelectField, CustomTextAreaField, ConfirmModal, CustomModal, CustomForm, CustomTextField, CustomButton},
  data() {
    return {
      clientId: undefined,
      inputs: {
        name: {label: 'Nom', value: '', type: 'text', required: true},
        type: {label: 'Type de client', value: CLIENT_TYPE_ENUM.UNKNOWN.value, selectValues: CLIENT_TYPE_ENUM_ARRAY, required: true},
        vatStatus: {label: 'Statut TVA du client', value: CLIENT_VAT_STATUS_ENUM.UNKNOWN.value, type: 'select', selectValues: CLIENT_VAT_STATUS_ENUM_ARRAY},
        siret: {label: 'Siret', value: '', type: 'text', mask: SIRET_MASK},
        siren: {label: 'Siren', value: '', type: 'text', mask: SIREN_MASK},
        other: {label: 'Informations supplémentaires', value: '', type: 'text'},
        status: {label: 'Statut', value: '', type: 'select', selectValues: CLIENT_STATUS_ENUM_ARRAY, required: true},
        description: {label: 'Description', value: '', type: 'text'},
        phone: {label: 'Téléphone', value: '', type: 'text'},
        mail: {label: 'Mail', value: '', type: 'text'},
        structuredAddress: {street: '', streetNumber: '', addressLine2: '', postalCode: '', city: '', country: '', countryCode: ''},
        address: {value: ''},
        clientInformation: {label: 'Information client (va disparaître)', value: '', readOnly: true},
        contact: {label: 'Contact', value: '', type: 'text'},
        lastContact: {label: 'Dernier contact', value: '', type: 'date'},
        note: {label: 'Note', value: ''},
        state: { value: undefined, left: ACTIVATION_STATUS_ENUM.DEACTIVATED, right: ACTIVATION_STATUS_ENUM.ACTIVATED, label: "Etat" },
      },
      buttons:{
        submit: {text: "Modifier"},
        cancel: {text: "Annuler"},
      },
      isTerminateButtonDisplayed: false,
      openSection: 'identity',
      deleteConfirmText: DELETE_CONFIRM_TEXT
    };
  },
  watch: {
    'inputs.type.value'(type) {
      this.updateSiretConstraints(type);
    },
    'inputs.siret.value'(siret) {
      const siren = sirenFromSiret(siret);
      if (siren) { this.inputs.siren.value = siren }
    }
  },
  methods: {
    // section : section de la fiche à ouvrir (identity, address, contact, followUp) ; par défaut, selon que la fiche est déjà identifiée
    openModal(client, section) {
      this.$refs.modal.openModal();
      this.$nextTick(() => this.$refs.modal.$el.querySelector('.content')?.scrollTo(0, 0));
      this.buttons.submit.disabled = false;
      // Fiche déjà identifiée (nom renseigné) : on s'ouvre sur le suivi commercial, sinon sur l'identité
      this.openSection = ['identity', 'address', 'contact', 'followUp'].includes(section)
          ? section
          : (client.structuredInformations && client.structuredInformations.name ? 'followUp' : 'identity');
      this.clientId = client.id
      this.inputs.name.value = client.structuredInformations.name;
      this.inputs.type.value = client.type || '';
      this.inputs.vatStatus.value = client.vatStatus || CLIENT_VAT_STATUS_ENUM.UNKNOWN.value;
      this.updateSiretConstraints(this.inputs.type.value);
      this.inputs.siret.value = normalizeIdentifier(client.structuredInformations.siret, SIRET_MASK.maxDigits);
      this.inputs.siren.value = normalizeIdentifier(client.structuredInformations.siren, SIREN_MASK.maxDigits);
      this.inputs.other.value = client.structuredInformations.other;
      this.inputs.status.value = client.status;
      this.inputs.description.value = client.description;
      this.inputs.phone.value = client.structuredInformations.phoneNumber;
      this.inputs.mail.value = client.structuredInformations.mail;
      this.inputs.clientInformation.value = client.clientInformation;
      this.inputs.structuredAddress = client.structuredAddress || {street: '', streetNumber: '', addressLine2: '', postalCode: '', city: '', country: '', countryCode: ''};
      this.inputs.address.value = '';
      this.inputs.contact.value = client.contact;
      this.inputs.lastContact.value = client.lastContact;
      this.inputs.note.value = client.note || '';
      this.inputs.state.value = client.state;
    },
    // SIRET facultatif (l'erreur est levée à la génération de la facture) ; s'il est saisi pour une entreprise française, il doit être complet (14 chiffres)
    updateSiretConstraints(type) {
      const isFrenchCompany = type === CLIENT_TYPE_ENUM.FRENCH_COMPANY.value;
      this.inputs.siret.minDigits = isFrenchCompany ? SIRET_MASK.maxDigits : 0;
    },
    // L'archivage est réversible mais demande une confirmation courte ; le désarchivage est immédiat
    async archiveClient() {
      const res = await this.$refs.archiveConfirmModal.displayChoice(() => ClientController.deactivate(this.clientId));
      if (res !== false && !(res && res.errors)) {
        (new NotificationService).addNotification("success", "Client archivé");
        this.terminate();
      }
    },
    async unarchiveClient() {
      const res = await ClientController.activate(this.clientId);
      if (!(res && res.errors)) {
        (new NotificationService).addNotification("success", "Client désarchivé");
        this.terminate();
      }
    },
    // La suppression est définitive : elle passe toujours par une confirmation
    async deleteClient() {
      // Une facture (acompte, facture ou avoir) empêche la suppression ; un simple devis donne lieu à un avertissement
      const impact = await ClientController.getDeletionImpact(this.clientId);
      if (!impact || impact.errors) { return }
      if (impact.invoicedBusinessesCount > 0) {
        (new NotificationService).addNotification("danger", "Impossible de supprimer ce client : une facture, une facture d’acompte ou une facture d’avoir lui est associée.");
        return;
      }
      this.deleteConfirmText = this.buildDeleteConfirmText(impact.quotesCount);
      await this.$nextTick();
      const res = await this.$refs.deleteConfirmModal.displayChoice(() => ClientController.delete(this.clientId));
      if (res !== false && !(res && res.errors)) {
        (new NotificationService).addNotification("success", "Client supprimé");
        this.terminate();
      }
    },
    buildDeleteConfirmText(quotesCount) {
      if (!quotesCount) { return DELETE_CONFIRM_TEXT }
      const warning = quotesCount === 1
        ? "Attention : ce client est associé à un devis. Il ne sera plus possible de générer ce devis, mais il restera dans l’historique."
        : `Attention : ce client est associé à ${quotesCount} devis. Il ne sera plus possible de les générer, mais ils resteront dans l’historique.`;
      return `${warning} ${DELETE_CONFIRM_TEXT}`;
    },
    toggleSection(section) {
      this.openSection = this.openSection === section ? '' : section;
    },
    closeModal(event) {
      this.$refs.modal?.closeModal(event);
    },
    terminate(){
      this.closeModal(true);
      this.callback();
    },
    async submitForm() {
      const res = await this.$refs.form.submitForm(() => ClientController.patch(this.clientId,{
        type : this.inputs.type.value,
        ...(this.isVatStatusDisplayed ? { vatStatus : this.inputs.vatStatus.value } : {}),
        status : this.inputs.status.value,
        description : this.inputs.description.value,
        structuredInformations : {
          name : this.inputs.name.value,
          phoneNumber : this.inputs.phone.value,
          mail : this.inputs.mail.value,
          siret : normalizeIdentifier(this.inputs.siret.value, SIRET_MASK.maxDigits),
          siren : normalizeIdentifier(this.inputs.siren.value, SIREN_MASK.maxDigits),
          other : this.inputs.other.value,
        },
        structuredAddress:  this.$refs.addressField.getValue(),
        contact : this.inputs.contact.value,
        lastContact : this.inputs.lastContact.value,
        note : this.inputs.note.value
      }));
      if (!res.errors) {
        (new NotificationService).addNotification("success", "Client modifié");
        this.terminate();
      }
    }

  }
}

</script>

<style lang="stylus" scoped>
@require '~/src/components/modals/clientForm.styl';

// Archivage : boutons à gauche du pied de modale
.footer-archive
  display flex
  align-items center
  gap 8px

.footer-archive-button
  display inline-flex
  align-items center
  justify-content center
  width CTRL_HEIGHT
  height CTRL_HEIGHT
  padding 0
  color CTRL_LABEL
  background white
  border 1px solid CTRL_BORDER
  border-radius CTRL_RADIUS
  cursor pointer
  transition background-color CTRL_EASE, border-color CTRL_EASE
  &:hover
    background CTRL_BG_HOVER
    border-color CTRL_BORDER_HOVER
  &.is-danger
    color CTRL_ERROR
    &:hover
      background CTRL_ERROR_BG
      border-color CTRL_ERROR
</style>

<style lang="stylus">
@require '~/src/components/modals/clientModalShell.styl';
</style>